"use client";

import type React from "react";
import { useEffect, useState } from "react";
import type { TemplateType } from "@/features/calendar/types/templates";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { Save } from "lucide-react";

import { toast } from "@/utils/customToast";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/NonPortalPopover.tsx";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import timezones from "@/features/auth/components/SignUpSteps/PreferencesForm/timezones";

// Define the schema for template update as a function that takes t
const updateTemplateSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .min(1, { message: t("forms:templates.validation.nameRequired") })
      .max(255, { message: t("forms:templates.validation.nameMaxLength") }),
    timezone: z
      .string({ required_error: t("forms:templates.validation.timezoneRequired") })
      .min(1, { message: t("forms:templates.validation.timezoneRequired") }),
  });

type UpdateTemplateFormValues = z.infer<ReturnType<typeof updateTemplateSchema>>;

interface UpdateTemplateDialogProps {
  templateId: string;
  templateName: string;
  templateType: TemplateType;
  templateTimezone: string;
  onSuccess?: () => void;
  children?: React.ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
}

export function UpdateTemplateDialog({
  templateId,
  templateName,
  templateType,
  templateTimezone,
  onSuccess,
  children,
  isOpen,
  onClose,
}: UpdateTemplateDialogProps) {
  const { t } = useTranslation(["common", "calendar", "forms", "timezones", "auth"]);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use either controlled or uncontrolled open state
  const dialogOpen = isOpen !== undefined ? isOpen : internalIsOpen;
  const handleOpenChange = (open: boolean) => {
    if (onClose && !open) {
      onClose();
    } else {
      setInternalIsOpen(open);
    }
  };

  const form = useForm<UpdateTemplateFormValues>({
    resolver: zodResolver(updateTemplateSchema(t)),
    defaultValues: {
      name: templateName,
      timezone: templateTimezone,
    },
  });

  // Reset form when dialog opens with current template name
  useEffect(() => {
    if (dialogOpen) {
      form.reset({ name: templateName, timezone: templateTimezone });
    }
  }, [dialogOpen, templateName, templateTimezone, form]);

  async function onSubmit(data: UpdateTemplateFormValues) {
    if (data.name === templateName && data.timezone === templateTimezone) {
      handleOpenChange(false);

      return;
    }

    setIsSubmitting(true);
    try {
      await axiosApiInstance.put(`${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}`, data);

      toast.success(t("calendar:templates.updateSuccess"), {
        description: t("calendar:templates.templateUpdated"),
      });

      handleOpenChange(false);
      form.reset({ name: templateName, timezone: templateTimezone });
      onSuccess?.();
    } catch (error: any) {
      console.error("Failed to update template:", error);

      // Handle validation errors from the API
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;

        Object.entries(apiErrors).forEach(([field, messages]) => {
          if (field === "name") {
            form.setError(field as "name", {
              type: "server",
              message: Array.isArray(messages) ? messages[0] : messages,
            });
          }
        });
      } else {
        toast.error(t("common:error"), {
          description: t("common:errors.genericErrorTitle"),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}

      <DialogContent aria-describedby={undefined} className="bg-background sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-accent-foreground text-xl">
            {t("calendar:templates.updateTemplate")}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 flex items-center gap-2">
          <Badge
            className={`${
              templateType === "weekly"
                ? "bg-muted/20 text-muted-foreground hover:bg-muted/30"
                : "bg-accent text-accent-foreground hover:bg-accent/30"
            }`}
          >
            {templateType === "weekly" ? (
              <Icon
                className="mr-1"
                height={"0.75rem"}
                icon="tabler:calendar-week"
                width={"0.75rem"}
              />
            ) : (
              <Icon className="mr-1" height={"0.75rem"} icon="tabler:calendar" width={"0.75rem"} />
            )}
            {t(`calendar:templates.${templateType}`)}
          </Badge>
          <span className="text-muted-foreground text-sm">
            {t("calendar:templates.typeCannotBeChanged")}
          </span>
        </div>

        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-accent-foreground">
                    {t("calendar:templates.templateName")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="bg-foreground"
                      placeholder={t("calendar:templates.templateNamePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t("calendar:templates.templateNameDescription")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-accent-foreground">
                    {t("calendar:templates.timezoneLabel")}
                  </FormLabel>
                  <FormDescription>
                    {t("calendar:templates.timezoneDescriptionDetail")}
                  </FormDescription>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          className="bg-foreground hover:bg-muted/30 flex w-full items-center justify-between gap-2 text-left font-normal"
                          role="combobox"
                          variant="outline"
                        >
                          <span className="flex items-center gap-2 text-sm">
                            <span className="iconify tabler--globe" />
                            {field.value
                              ? t(
                                  // @ts-ignore
                                  `timezones:${field.value}`,
                                  {
                                    defaultValue: field.value,
                                  },
                                )
                              : t("calendar:templates.timezonePlaceholder")}
                          </span>
                          <Icon className="h-4 w-4 opacity-70" icon="tabler:chevron-down" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="p-0" side="bottom">
                      <Command>
                        <CommandInput placeholder={t("auth:signUp.searchTimezone")} />
                        <CommandList>
                          <CommandEmpty>{t("auth:signUp.noTimezoneFound")}</CommandEmpty>
                          <CommandGroup>
                            {timezones.map((timezoneOption) => (
                              <CommandItem
                                key={timezoneOption}
                                className={"text-accent-foreground"}
                                value={timezoneOption}
                                onSelect={() => {
                                  form.setValue("timezone", timezoneOption, {
                                    shouldValidate: true,
                                  });
                                }}
                              >
                                {t(
                                  // @ts-ignore
                                  `timezones:${timezoneOption}`,
                                  {
                                    defaultValue: timezoneOption,
                                  },
                                )}
                                {timezoneOption === field.value && (
                                  <Icon className="ml-auto h-4 w-4" icon="tabler:check" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                disabled={isSubmitting}
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t("common:cancel")}
              </Button>
              <Button disabled={isSubmitting} type="submit" variant={"foreground"}>
                {isSubmitting ? (
                  <div className="flex items-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      className="mr-2"
                      transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                    >
                      <Icon height={"1rem"} icon="tabler:loader-2" width={"1rem"} />
                    </motion.div>
                    {t("common:updating")}
                  </div>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {t("common:save")}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
